import { useState } from 'react'
import { applyTheme, type Theme } from '../theme'

function readTheme(): Theme {
  const fromDom = document.documentElement.dataset.theme
  if (fromDom === 'light' || fromDom === 'dark') return fromDom
  // The unstamped root IS light, so no stamp means light. Same default as
  // index.html and theme.ts — three copies of one rule is two too many, and
  // if this ever needs changing, collapse them first.
  return 'light'
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readTheme)

  function set(next: Theme) {
    applyTheme(next)
    setThemeState(next)
  }

  function toggle() {
    set(theme === 'light' ? 'dark' : 'light')
  }

  return { theme, setTheme: set, toggleTheme: toggle }
}

// One switch, not two buttons. A segmented pair reads as a single toggle, so
// clicking the half that is already active does nothing and it feels like it
// took two clicks to work. One click here is always a flip, and the knob sits
// over the state you are in.
export function ThemeToggle() {
  const { theme, toggleTheme } = useTheme()
  const dark = theme === 'dark'

  return (
    <div className="theme-toggle">
      {/* The visible label names what the switch turns ON, not which state we
          are in, so it is contained in the accessible name below — a voice
          user saying "click Dark" has something to match. */}
      <span className="theme-toggle-label" id="theme-switch-label">
        Dark
      </span>
      <button
        type="button"
        className="theme-switch"
        role="switch"
        aria-checked={dark}
        // A switch's name is constant and aria-checked carries the state. A
        // name that flipped to "switch to light" while aria-checked was true
        // announced "light, switch, on" with dark on — the two contradicting
        // each other. The title still says what a click does; that is a
        // tooltip, not a name.
        aria-labelledby="theme-switch-label"
        title={dark ? 'Switch to the light theme' : 'Switch to the dark theme'}
        onClick={toggleTheme}
      >
        <span className="theme-knob" aria-hidden />
        <span className="theme-track" aria-hidden>
          <svg className="sun" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
          </svg>
          <svg className="moon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        </span>
      </button>
    </div>
  )
}
